"use client";
import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Menu, X, Compass, Search, Heart } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocaleRouter } from '@/hooks/useLocaleRouter';
import { useUser } from '@clerk/nextjs';
import UserDropdown from './UserDropdown';
import { trpc } from '@/lib/trpc/client';
import { getOrCreateVisitorId } from '@/lib/visitor-id';
import SearchFlyover from "./SearchFlyover";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const pathname = usePathname();
  const { t, i18n } = useTranslation();
  const { createLocalizedPath } = useLocaleRouter();
  const { isSignedIn } = useUser();

  // Get visitor ID for anonymous users
  const visitorId = !isSignedIn ? getOrCreateVisitorId() : undefined;

  // Get wishlist count (works for both authenticated and anonymous users)
  const { data: wishlistCount } = trpc.wishlist.getCount.useQuery(
    { visitorId },
    {
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    // Set initial scroll state
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem("lang");
    if (stored && stored !== i18n.language) {
      i18n.changeLanguage(stored);
    }
  }, [i18n]);

  const navLinks = [
    { name: t("nav.about"), path: createLocalizedPath("/about") },
    { name: t("nav.signatureJourneys"), path: createLocalizedPath("/signature-journeys") },
    { name: t("nav.travelThemes"), path: createLocalizedPath("/travel-themes") },
    { name: t("nav.travelCircle"), path: createLocalizedPath("/travel-circle") },
    { name: t("nav.journal"), path: createLocalizedPath("/journal") },
  ];

  // Helper function to check if a link is active
  const isActiveLink = (linkPath: string) => {
    if (!pathname) return false;
    
    // Remove locale prefix for comparison
    const cleanPathname = pathname.replace(/^\/[a-z]{2}/, '') || '/';
    const cleanLinkPath = linkPath.replace(/^\/[a-z]{2}/, '') || '/';
    
    // Special handling for home page
    if (cleanLinkPath === '/' && cleanPathname === '/') return true;
    
    // For other paths, check if current pathname starts with the link path
    return cleanPathname.startsWith(cleanLinkPath) && cleanLinkPath !== '/';
  };

  // Helper to determine if we are on the home page or a locale root
  const isHomeOrLocaleRoot =
    pathname === "/" || 
    pathname === "/en" || 
    pathname === "/de" || 
    pathname === "/uk" ||
    (pathname && pathname.match(/^\/[a-z]{2}$/));
  
  // Debug logging
  console.log('Navbar Debug:', { 
    pathname, 
    isHomeOrLocaleRoot, 
    isScrolled, 
    isOpen,
    shouldBeTransparent: isHomeOrLocaleRoot && !isScrolled && !isOpen
  });

  // When mobile menu is open, always use white for brand
  const brandTextColor =
    isOpen
      ? "text-gray-900"
      : isHomeOrLocaleRoot && !isScrolled
        ? "text-white"
        : "text-gray-900";

  const brandIconColor =
    isOpen
      ? "text-gray-900"
      : isHomeOrLocaleRoot && !isScrolled
        ? "text-white"
        : "text-gray-900";

  const navClassName = `fixed w-full z-50 transition-all duration-300 ${
    isHomeOrLocaleRoot
      ? isOpen
        ? "bg-white shadow-md"
        : isScrolled
          ? "bg-white shadow-md"
          : "bg-transparent !bg-transparent"
      : "bg-white shadow-sm"
  }`;
  
  console.log('Navbar className:', navClassName);
  console.log('Navbar style:', isHomeOrLocaleRoot && !isScrolled && !isOpen ? { backgroundColor: 'transparent' } : {});
  
  const navStyle = isHomeOrLocaleRoot && !isScrolled && !isOpen 
    ? { backgroundColor: 'transparent' } 
    : {};

  return (
    <nav className={navClassName} style={navStyle}>
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="flex justify-between items-center py-2 sm:py-3 lg:py-4">
          <Link href={createLocalizedPath("/")} className="flex items-center space-x-2 flex-shrink-0 group touch-target">
            <Compass
              className={`h-8 w-8 sm:h-9 sm:w-9 lg:h-10 lg:w-10 transition-all duration-300 group-hover:rotate-12 ${brandIconColor}`}
            />
            <span
              className={`text-xl sm:text-2xl lg:text-2xl font-serif font-semibold transition-colors duration-300 ${brandTextColor}`}
            >
              {t("brand")}
            </span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-8 lg:space-x-4">
            {navLinks.map((link) => {
              const isActive = isActiveLink(link.path);
              return (
                <Link
                  key={link.path}
                  href={link.path}
                  className={`text-sm font-medium tracking-wide transition-all duration-200 relative group whitespace-nowrap ${
                    isHomeOrLocaleRoot && !isScrolled
                      ? "text-white hover:text-white/90"
                      : "text-gray-900 hover:text-gray-700"
                  }`}
                >
                  {link.name}
                  <span className={`absolute -bottom-1 left-0 h-0.5 transition-all duration-300 ${
                    isActive
                      ? "w-full"
                      : "w-0 group-hover:w-full"
                  } ${
                    isHomeOrLocaleRoot && !isScrolled
                      ? "bg-white"
                      : "bg-gray-900"
                  }`}></span>
                </Link>
              );
            })}
            <button
              onClick={() => setIsSearchOpen(true)}
              className={`p-2 rounded-lg transition-all duration-200 hover:bg-black/5 ${
                isHomeOrLocaleRoot && !isScrolled
                  ? "text-white hover:bg-white/10"
                  : "text-gray-900 hover:bg-gray-100"
              }`}
              aria-label="Search"
            >
              <Search className="h-5 w-5" />
            </button>
            <Link
              href={createLocalizedPath("/u/wishlist")}
              className={`p-2 rounded-lg transition-all duration-200 hover:bg-black/5 relative ${
                isHomeOrLocaleRoot && !isScrolled
                  ? "text-white hover:bg-white/10"
                  : "text-gray-900 hover:bg-gray-100"
              }`}
              aria-label="Wishlist"
              title={t("nav.wishlist")}
            >
              <Heart className="h-5 w-5" />
              {wishlistCount && wishlistCount.count > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 text-white text-[10px] rounded-full h-4 w-4 flex items-center justify-center font-bold"
                  style={{ backgroundColor: '#cdc50a' }}
                >
                  {wishlistCount.count}
                </span>
              )}
            </Link>
            {isSignedIn && <UserDropdown />}
            <Link
              href={createLocalizedPath("/contact")}
              className="text-white px-6 lg:px-8 py-2.5 lg:py-3 rounded-full text-sm lg:text-base font-bold hover:shadow-lg hover:scale-105 transition-all duration-200 whitespace-nowrap touch-target"
              style={{ backgroundColor: '#cdc50a' }}
            >
              {t("nav.planJourney")}
            </Link>
          </div>

          {/* Mobile Menu Buttons */}
          <div className="lg:hidden flex items-center space-x-2">
            <button
              onClick={() => setIsSearchOpen(true)}
              className={`p-2.5 rounded-lg transition-all duration-300 hover:bg-black/5 touch-target ${
                isHomeOrLocaleRoot && !isScrolled && !isOpen
                  ? "text-white hover:bg-white/10"
                  : "text-gray-900"
              }`}
              aria-label="Search"
            >
              <Search className="h-6 w-6" />
            </button>
            <Link
              href={createLocalizedPath("/u/wishlist")}
              className={`p-2.5 rounded-lg transition-all duration-300 hover:bg-black/5 touch-target relative ${
                isHomeOrLocaleRoot && !isScrolled && !isOpen
                  ? "text-white hover:bg-white/10"
                  : "text-gray-900"
              }`}
              aria-label="Wishlist"
            >
              <Heart className="h-6 w-6" />
              {wishlistCount && wishlistCount.count > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 text-white text-[10px] rounded-full h-4 w-4 flex items-center justify-center font-bold"
                  style={{ backgroundColor: '#cdc50a' }}
                >
                  {wishlistCount.count}
                </span>
              )}
            </Link>
            {isSignedIn && <UserDropdown />}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`p-2.5 rounded-lg transition-all duration-300 hover:bg-black/5 touch-target ${
                isHomeOrLocaleRoot && !isScrolled && !isOpen
                  ? "text-white hover:bg-white/10"
                  : "text-gray-900"
              }`}
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
          <div className="lg:hidden border-t bg-white border-gray-200 transition-all duration-300">
          <div className="px-5 sm:px-6 py-6 space-y-2">
            {navLinks.map((link) => {
              const isActive = isActiveLink(link.path);
              return (
                <Link
                  key={link.path}
                  href={link.path}
                  className={`block text-base sm:text-lg font-medium transition-all duration-200 py-3.5 px-4 rounded-lg touch-target ${
                    isActive 
                      ? "text-gray-900 font-semibold bg-gray-50" 
                      : "text-gray-900 hover:text-gray-700 hover:translate-x-1 hover:bg-gray-50"
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  {link.name}
                </Link>
              );
            })}
            <Link
              href={createLocalizedPath("/u/wishlist")}
              className="flex items-center justify-between text-base sm:text-lg font-medium text-gray-900 hover:text-gray-700 transition-all duration-200 py-3.5 px-4 rounded-lg hover:translate-x-1 hover:bg-gray-50 touch-target"
              onClick={() => setIsOpen(false)}
            >
              <span>{t("nav.wishlist")}</span>
              {wishlistCount && wishlistCount.count > 0 && (
                <span
                  className="text-white text-xs rounded-full h-6 w-6 flex items-center justify-center font-bold"
                  style={{ backgroundColor: '#cdc50a' }}
                >
                  {wishlistCount.count}
                </span>
              )}
            </Link>
            <div className="pt-5 border-t border-gray-100">
              <Link
                href={createLocalizedPath("/contact")}
                className="block text-white px-6 py-4 rounded-full text-center text-base sm:text-lg font-bold transition-all duration-200 shadow-md touch-target"
                style={{ backgroundColor: '#cdc50a' }}
                onClick={() => setIsOpen(false)}
              >
                {t("nav.planJourney")}
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Search Flyover */}
      <SearchFlyover isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </nav>
  );
};

export default Navbar;
